import styled, { keyframes } from 'styled-components';
import { useEffect, useState } from 'react';

const rotateAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(90deg); }
`;

const OverlayContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: #d4d4d4;
  z-index: 9999;
`;

const DeviceIcon = styled.div`
  width: 80px;
  height: 120px;
  border: 4px solid #878787;
  border-radius: 12px;
  position: relative;
  margin-bottom: 20px;
  animation: ${rotateAnimation} 2s infinite ease-in-out;
  background-color: white;

  &::before {
    content: '';
    position: absolute;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 4px;
    background-color: #b5b5b5;
    border-radius: 50%;
  }
`;

const ScreenContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 80px;
  border: 2px solid #989898;
  border-radius: 4px;
  opacity: 0.3;
`;

const Message = styled.div`
  color: #272727;
  font-size: 1.5em;
  text-align: center;
  padding: 0 20px;
  max-width: 300px;
`;

export const OrientationOverlay = (): JSX.Element | null => {
  const [isPortrait, setIsPortrait] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [hasTouch, setHasTouch] = useState<boolean>(false);

  useEffect(() => {
    // Check for touch support
    const checkTouchSupport = (): void => {
      const hasTouchSupport = Boolean(
        'ontouchstart' in window || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0)
      );
      setHasTouch(hasTouchSupport);
    };

    // Check for mobile device using media queries
    const mobileQuery = window.matchMedia('(max-width: 64rem)');
    const portraitQuery = window.matchMedia('(orientation: portrait)');

    const handleMobileChange = (e: MediaQueryListEvent | MediaQueryList): void => {
      setIsMobile(e.matches);
    };

    const handleOrientationChange = (e: MediaQueryListEvent | MediaQueryList): void => {
      setIsPortrait(e.matches);
    };

    // Initial checks
    checkTouchSupport();
    handleMobileChange(mobileQuery);
    handleOrientationChange(portraitQuery);

    // Add event listeners
    mobileQuery.addEventListener('change', handleMobileChange);
    portraitQuery.addEventListener('change', handleOrientationChange);
    window.addEventListener('resize', checkTouchSupport);

    // Cleanup
    return (): void => {
      mobileQuery.removeEventListener('change', handleMobileChange);
      portraitQuery.removeEventListener('change', handleOrientationChange);
      window.removeEventListener('resize', checkTouchSupport);
    };
  }, []);

  // Only show overlay if:
  // 1. Device is mobile (width <= 64rem)
  // 2. Device has touch support
  // 3. Device is in portrait mode
  if (!isMobile || !isPortrait || !hasTouch) {
    return null;
  }

  return (
    <OverlayContainer>
      <DeviceIcon>
        <ScreenContent />
      </DeviceIcon>
      <Message>Please rotate your device to landscape mode to continue</Message>
    </OverlayContainer>
  );
};
